import React from 'react';
import { useTranslation } from 'react-i18next';
import {
  IconPhoneCall,
  IconTransfer,
  IconMessage,
  IconPhoneOff,
  IconUsers,
  IconBan,
  IconPhoneX,
  IconUserMinus,
  IconCalendarEvent,
} from '@tabler/icons-react';
import type { SessionStats } from '../types/dashboard.types';
import { Flame } from 'lucide-react';

interface StatsCardsProps {
  stats: SessionStats;
  totalContacts: number | null;
  onClickStat: (type: string, filter: string) => void;
  fuegoLeadCount?: number | null;
}

interface StatCardProps {
  label: string;
  value: number | string;
  subtext?: string;
  icon: React.ReactNode;
  iconBgColor: string;
  valueColor?: string;
  onSelected: (type: string, filter: string) => void;
  type: string;
}

const DISPOSITION_OPTIONS = [
  { value: 'NA', label: 'No Answer' },
  { value: 'SA', label: 'Set Appointments' },
  { value: 'LVM', label: 'Left a Voicemail' },
  { value: 'PTNC', label: 'Phone Tree' },
  { value: 'GC', label: 'GK Conversations' },
  { value: 'OTR', label: 'Other' },
  { value: 'PR', label: 'Phone Refusal (hangup)' },
  { value: 'OO', label: 'Opt Out' },
  { value: 'RAC', label: 'Referred' },
  { value: 'NLC', label: 'No Longer w/ Company' },
  { value: 'WN', label: 'Wrong Number' },
];

const ACTIONS_OPTIONS = [
  { value: 'Transferred', label: 'transferred' },
];

function StatCard ({ label, value, subtext, icon, iconBgColor, valueColor = 'text-foreground', onSelected, type }: StatCardProps): React.JSX.Element {
  return (
    <button
      className="bg-card rounded-xl border border-border p-4 min-h-[120px] cursor-pointer hover:bg-muted transition"
      onClick={() => {
        if (value !== 0) {
          if (type === 'action') {
            const selectedCase: string =  ACTIONS_OPTIONS.find(a => a.value === label)?.label ?? 'all';
            onSelected('action', selectedCase);
            onSelected('disposition', 'all');
          } else {
            const selectedCase: string = DISPOSITION_OPTIONS.find(o => o.label === label)?.value ?? 'all';
            onSelected('disposition', selectedCase);
            onSelected('action', 'all');
          }
        }
      }}
    >
      <div className="flex items-center justify-between mb-3">
        <span className="text-sm text-muted-foreground">{label}</span>
        <div className={`p-2 rounded-lg ${iconBgColor}`}>
          {icon}
        </div>
      </div>
      <div>
        <p className={`text-3xl font-bold ${valueColor}`}>{value}</p>
        {subtext !== undefined && subtext !== '' && (
          <p className={`text-sm mt-1 ${valueColor} opacity-80`}>{subtext}</p>
        )}
      </div>
    </button>
  );
}

export default function StatsCards ({ stats, onClickStat, fuegoLeadCount }: StatsCardsProps): React.JSX.Element {
  const { t } = useTranslation();
  const getPercentage = (value: number): number =>
    stats.totalCalls > 0 ? (value / stats.totalCalls) * 100 : 0;

  return (
    <div className="space-y-4">
      {/* First Row */}
      <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-4">
        <StatCard
          label={t('dashboard.statsCards.answered')}
          value={stats.answered}
          subtext={t('dashboard.statsCards.rate', { value: getPercentage(stats.answered).toFixed(1) })}
          icon={<IconPhoneCall className="h-5 w-5 text-green-500" />}
          iconBgColor="bg-green-500/10"
          valueColor="text-green-500"
          onSelected={onClickStat}
          type={'action'}
        />
        <StatCard
          label={t('dashboard.statsCards.transferred')}
          value={stats.transferred}
          subtext={t('dashboard.statsCards.rate', { value: getPercentage(stats.transferred).toFixed(1) })}
          icon={<IconTransfer className="h-5 w-5 text-blue-500" />}
          iconBgColor="bg-blue-500/10"
          valueColor="text-blue-500"
          onSelected={onClickStat}
          type={'action'}

        />
        <StatCard
          label={t('dashboard.statsCards.gkConversations')}
          value={stats.gatekeeperConversation}
          subtext={t('dashboard.statsCards.percent', { value: getPercentage(stats.gatekeeperConversation).toFixed(1) })}
          icon={<IconUsers className="h-5 w-5 text-cyan-500" />}
          iconBgColor="bg-cyan-500/10"
          valueColor="text-cyan-500"
          onSelected={onClickStat}
          type={'disposition'}
        />
        <StatCard
          label={t('dashboard.statsCards.leftVoicemail')}
          value={stats.leftVoicemail}
          subtext={t('dashboard.statsCards.percent', { value: getPercentage(stats.leftVoicemail).toFixed(1) })}
          icon={<IconMessage className="h-5 w-5 text-orange-500" />}
          iconBgColor="bg-orange-500/10"
          valueColor="text-orange-500"
          onSelected={onClickStat}
          type={'disposition'}
        />
        <StatCard
          label={t('dashboard.statsCards.noAnswer')}
          value={stats.noAnswer}
          subtext={t('dashboard.statsCards.percent', { value: getPercentage(stats.noAnswer).toFixed(1) })}
          icon={<IconPhoneOff className="h-5 w-5 text-red-500" />}
          iconBgColor="bg-red-500/10"
          valueColor="text-red-500"
          onSelected={onClickStat}
          type={'disposition'}
        />
        <StatCard
          label={t('dashboard.statsCards.fuegoLeads')}
          value={fuegoLeadCount ?? 0}
          subtext={t('dashboard.statsCards.percent', { value: getPercentage(fuegoLeadCount ?? 0).toFixed(1) })}
          icon={<Flame className="h-5 w-5 text-amber-500" />}
          iconBgColor="bg-amber-500/10"
          valueColor="text-amber-500"
          onSelected={onClickStat}
          type={'action'}
        />
      </div>

      {/* Second Row */}
      <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-4">
        <StatCard
          label={t('dashboard.statsCards.wrongNumber')}
          value={stats.wrongNumber}
          subtext={t('dashboard.statsCards.percent', { value: getPercentage(stats.wrongNumber).toFixed(1) })}
          icon={<IconPhoneX className="h-5 w-5 text-gray-400" />}
          iconBgColor="bg-gray-500/10"
          onSelected={onClickStat}
          type={'disposition'}
        />
        <StatCard
          label={t('dashboard.statsCards.phoneTree')}
          value={stats.phoneTreeNoConnection}
          subtext={t('dashboard.statsCards.percent', { value: getPercentage(stats.phoneTreeNoConnection).toFixed(1) })}
          icon={<IconPhoneOff className="h-5 w-5 text-purple-500" />}
          iconBgColor="bg-purple-500/10"
          valueColor="text-purple-500"
          onSelected={onClickStat}
          type={'disposition'}
        />
        <StatCard
          label={t('dashboard.statsCards.noLongerWithCompany')}
          value={stats.noLongerWithCompany}
          subtext={t('dashboard.statsCards.percent', { value: getPercentage(stats.noLongerWithCompany).toFixed(1) })}
          icon={<IconUserMinus className="h-5 w-5 text-pink-500" />}
          iconBgColor="bg-pink-500/10"
          valueColor="text-pink-500"
          onSelected={onClickStat}
          type={'disposition'}
        />
        <StatCard
          label={t('dashboard.statsCards.referred')}
          value={stats.referredAnotherContact}
          subtext={t('dashboard.statsCards.percent', { value: getPercentage(stats.referredAnotherContact).toFixed(1) })}
          icon={<IconUsers className="h-5 w-5 text-indigo-500" />}
          iconBgColor="bg-indigo-500/10"
          valueColor="text-indigo-500"
          onSelected={onClickStat}
          type={'disposition'}
        />
        <StatCard
          label={t('dashboard.statsCards.optOut')}
          value={stats.optOut}
          subtext={t('dashboard.statsCards.percent', { value: getPercentage(stats.optOut).toFixed(1) })}
          icon={<IconBan className="h-5 w-5 text-red-400" />}
          iconBgColor="bg-red-500/10"
          valueColor="text-red-400"
          onSelected={onClickStat}
          type={'disposition'}
        />
        <StatCard
          label={t('dashboard.statsCards.setAppointments')}
          value={stats.setAppointments}
          subtext={t('dashboard.statsCards.conversion', { value: getPercentage(stats.setAppointments).toFixed(1) })}
          icon={<IconCalendarEvent className="h-5 w-5 text-green-400" />}
          iconBgColor="bg-green-500/10"
          valueColor="text-green-400"
          onSelected={onClickStat}
          type={'disposition'}
        />
      </div>
    </div>
  );
}
